import {
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  Input,
  OnDestroy,
  QueryList,
} from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DsStatTabComponent } from "../ds-stat-tab/ds-stat-tab.component";

/**
 * Groups a set of `ds-stat-tab`s and adds the responsive strip behavior for free:
 * equal-width boxes on desktop, and a horizontal snap-scroller with a carousel
 * indicator on mobile that auto-centers the active pill on selection.
 *
 * Usage:
 * ```html
 * <ds-stat-tab-group ariaLabel="Filtros">
 *   <ds-stat-tab *ngFor="..." [fullWidth]="true" [active]="..." (selected)="..."></ds-stat-tab>
 * </ds-stat-tab-group>
 * ```
 */
@Component({
  selector: "ds-stat-tab-group",
  templateUrl: "./ds-stat-tab-group.component.html",
  styleUrls: ["./ds-stat-tab-group.component.scss"],
})
export class DsStatTabGroupComponent implements AfterContentInit, OnDestroy {
  /** Accessible label for the tablist container. */
  @Input() ariaLabel?: string;

  @ContentChildren(DsStatTabComponent) tabs!: QueryList<DsStatTabComponent>;
  @ContentChildren(DsStatTabComponent, { read: ElementRef }) tabElements!: QueryList<ElementRef<HTMLElement>>;

  private readonly destroy$ = new Subject<void>();
  private readonly resubscribe$ = new Subject<void>();

  ngAfterContentInit(): void {
    this.bindSelection();
    this.tabs.changes.pipe(takeUntil(this.destroy$)).subscribe(() => this.bindSelection());
    // Center the initially active tab once the strip is laid out.
    setTimeout(() => this.centerActive());
  }

  ngOnDestroy(): void {
    this.resubscribe$.next();
    this.resubscribe$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private bindSelection(): void {
    this.resubscribe$.next();
    this.tabs.toArray().forEach((tab, index) => {
      tab.selected.pipe(takeUntil(this.resubscribe$), takeUntil(this.destroy$)).subscribe(() => this.center(index));
    });
  }

  private centerActive(): void {
    const index = this.tabs.toArray().findIndex((tab) => tab.active);
    if (index >= 0) {
      this.center(index);
    }
  }

  private center(index: number): void {
    const element = this.tabElements.toArray()[index]?.nativeElement;
    element?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }
}
